import { CONTRACT_TRANSFER_TAX_ELECTION } from '@/config/constants';
import { productAddressParts } from '@/utils/productAddress';
import type { Contract } from '@/types/ContractType';

const STATE_BY_ABBR: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};
const FULL_NAMES = new Set(Object.values(STATE_BY_ABBR));

/** "TX", "tx", "Texas", "texas " -> "Texas"; unknown -> ''. */
export function normalizeStateName(input?: string | null): string {
  const raw = String(input || '').trim();
  if (!raw) return '';
  const abbr = STATE_BY_ABBR[raw.toUpperCase()];
  if (abbr) return abbr;
  const lower = raw.toLowerCase();
  for (const name of FULL_NAMES) {
    if (name.toLowerCase() === lower) return name;
  }
  if (lower === 'washington dc' || lower === 'washington, dc') {
    return 'District of Columbia';
  }
  return '';
}

/**
 * The property's state for contract purposes. The property address comes
 * first — state sections used to read only the agent-entered
 * agentSellerInfo.propertyState, so buyer-drafted contracts never got any
 * state-specific terms.
 */
export function resolveContractState(contract?: Contract | null): string {
  const c = contract as any;
  const fromAddress = productAddressParts(c?.product).state;
  return (
    normalizeStateName(fromAddress) ||
    normalizeStateName(c?.sellerPropertyInfo?.propertyState) ||
    normalizeStateName(c?.agentSellerInfo?.propertyState) ||
    ''
  );
}

export type TransferTaxPayer = 'seller' | 'buyer' | 'split' | 'none';

/**
 * Customary payer of the state real estate transfer / deed / documentary
 * stamp tax, used when the buyer makes no election. "none" = no state
 * transfer tax. Custom only — parties may agree otherwise. Sources: NAR state
 * transfer tax summary (via Land Trust Alliance) for which states tax
 * transfers; customary payer per common state practice. States marked
 * NEEDS_CONFIRMATION vary by county and should be confirmed.
 */
export const TRANSFER_TAX_CUSTOM: Record<string, TransferTaxPayer> = {
  Alabama: 'buyer',
  Alaska: 'none',
  Arizona: 'none',
  Arkansas: 'split', // NEEDS_CONFIRMATION
  California: 'seller',
  Colorado: 'buyer', // NEEDS_CONFIRMATION (0.01% documentary fee)
  Connecticut: 'seller',
  Delaware: 'split',
  'District of Columbia': 'split',
  Florida: 'seller',
  Georgia: 'seller',
  Hawaii: 'seller', // NEEDS_CONFIRMATION
  Idaho: 'none',
  Illinois: 'seller',
  Indiana: 'none',
  Iowa: 'seller',
  Kansas: 'none',
  Kentucky: 'seller',
  Louisiana: 'none',
  Maine: 'split',
  Maryland: 'split',
  Massachusetts: 'seller',
  Michigan: 'seller',
  Minnesota: 'seller',
  Mississippi: 'none',
  Missouri: 'none',
  Montana: 'none',
  Nebraska: 'seller',
  Nevada: 'seller', // NEEDS_CONFIRMATION (split is common in some counties)
  'New Hampshire': 'split',
  'New Jersey': 'seller',
  'New Mexico': 'none',
  'New York': 'seller',
  'North Carolina': 'seller',
  'North Dakota': 'none',
  Ohio: 'seller',
  Oklahoma: 'seller',
  Oregon: 'none',
  Pennsylvania: 'split',
  'Rhode Island': 'seller',
  'South Carolina': 'seller',
  'South Dakota': 'seller',
  Tennessee: 'buyer',
  Texas: 'none',
  Utah: 'none',
  Vermont: 'buyer',
  Virginia: 'split',
  Washington: 'seller',
  'West Virginia': 'seller',
  Wisconsin: 'seller',
  Wyoming: 'none',
};

const ELECTION_TO_PAYER: Partial<Record<string, TransferTaxPayer>> = {
  [CONTRACT_TRANSFER_TAX_ELECTION.SELLER_PAYS_ALL]: 'seller',
  [CONTRACT_TRANSFER_TAX_ELECTION.BUYER_PAYS_EXCESS]: 'buyer',
  [CONTRACT_TRANSFER_TAX_ELECTION.SPLIT_BETWEEN_BUYER_AND_SELLER]: 'split',
};

/** The election option matching a state's custom (for pre-selecting the form). */
export function defaultTransferTaxElection(state: string): string {
  switch (TRANSFER_TAX_CUSTOM[normalizeStateName(state)]) {
    case 'buyer':
      return CONTRACT_TRANSFER_TAX_ELECTION.BUYER_PAYS_EXCESS;
    case 'split':
      return CONTRACT_TRANSFER_TAX_ELECTION.SPLIT_BETWEEN_BUYER_AND_SELLER;
    case 'seller':
      return CONTRACT_TRANSFER_TAX_ELECTION.SELLER_PAYS_ALL;
    default:
      return CONTRACT_TRANSFER_TAX_ELECTION.NOT_SELECTED;
  }
}

/**
 * The Settlement clause's transfer-tax sentence: the buyer's election when
 * made, otherwise the property state's custom. (It used to say "Seller will
 * pay all transfer taxes" regardless of the election.)
 */
export function transferTaxSentence(opts: {
  election?: string | null;
  state?: string | null;
}): string {
  const state = normalizeStateName(opts.state);
  const payer =
    (opts.election && ELECTION_TO_PAYER[opts.election]) ||
    (state ? TRANSFER_TAX_CUSTOM[state] : undefined) ||
    'seller';
  switch (payer) {
    case 'buyer':
      return 'Buyer will pay all transfer taxes and/or documentary stamp taxes.';
    case 'split':
      return 'Transfer taxes and/or documentary stamp taxes will be split equally between Buyer and Seller.';
    case 'none':
      return `${state} imposes no state real estate transfer tax; any local transfer taxes or recording charges will be paid according to local custom unless otherwise agreed.`;
    default:
      return 'Seller will pay all transfer taxes and/or documentary stamp taxes.';
  }
}
